// eslint-disable-next-line no-unused-vars
import React from 'react';
import { array, objectOf, shape, bool, func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Loading, SkeletonPlaceholder } from '@carbon/react';
import Carousel from 'react-multi-carousel';

import Img from 'Containers/Image/Image';
import useScreen from 'Hooks/useScreen';
import ButtonGroup from 'Components/Dashboard/CarouselButtonGroup';
import { AnalyticsLink } from 'Components/shared';

import BannerHPE from 'Assets/images/banner-hpe.png';
import BannerSustainableEnergy from 'Assets/images/banner-sustainable_energy.png';
import BannerNODAcademy from 'Assets/images/banner-nod_academy.png';
import BannerTPLink from 'Assets/images/banner-tp_link.png';

const StyledBanners = styled.section`
  position: relative;
  margin-top: 90px;
  padding-bottom: 25px;

  &::before {
    content: "";
    position: absolute;
    top: -70px;
    bottom: 0;
    left: -100%;
    right: -100%;
    background-color: #F3F3F3;
    z-index: -1;
  }

  .bannersSlider {
    margin: 0 -16px;

    .banner {
      width: 100%;
      height: 100%;
      padding: 0 16px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .cta {
        display: flex;
        align-items: end;
        justify-content: space-between;
        gap: 20px;
        margin-bottom: 20px;

        h3 {
          font-weight: 600;
          margin-bottom: 8px;
        }

        p {
          font-size: 14px;
          color: #8D8D8D;
        }

        button, a {
          background: #F47926;
          color: #fff;
          border-radius: 2px;
          padding: 8px 32px;
          border: none;
          cursor: pointer;
          display: flex;
          font-size: 14px;
          white-space: nowrap;
        }

        a {
          display: inline-block;
        }
      }

      img {
        max-width: 100%;
        object-fit: contain;
      }
    }
  }
`;

const StyledBannerPlaceholder = styled(SkeletonPlaceholder)`
  width: calc(100% - 20px);
  aspect-ratio: 2;
  height: auto;
`;

const renderBackupBanners = ({ t, actionHandlers }) => [
  <div className="banner hpe" key="hpe">
    <div className="cta">
      <div className="info">
        <h3>{ t('products.hpeTitle') }</h3>
        <p>{ t('products.hpeDescription') }</p>
      </div>
      <button
        type="button"
        onClick={ actionHandlers?.hpe?.onClick }
      >
        { actionHandlers?.hpe?.loading && <Loading active small withOverlay={ false } /> }
        { t('pages.seeMore') }
      </button>
    </div>
    <img src={ BannerHPE } alt="HPE Platform" />
  </div>,
  <div className="banner" key="sustainable-energy">
    <div className="cta">
      <div className="info">
        <h3>{ t('products.sustainableEnergyTitle') }</h3>
        <p>{ t('products.sustainableEnergyDescription') }</p>
      </div>
      <AnalyticsLink
        to="https://fondurimodernizare-nod.ro/"
        target="_blank"
        event={ { action: 'sustainable_energy_banner_clicked' } }
      >
        { t('pages.seeMore') }
      </AnalyticsLink>
    </div>
    <img src={ BannerSustainableEnergy } alt="NOD Sustainable Energy" />
  </div>,
  <div className="banner" key="academy">
    <div className="cta">
      <div className="info">
        <h3>{ t('products.academyTitle') }</h3>
        <p>{ t('products.academyDescription') }</p>
      </div>
      <AnalyticsLink
        to="https://nodacademy.ro/"
        target="_blank"
        event={ { action: 'nod_academy_banner_clicked' } }
      >
        { t('pages.seeMore') }
      </AnalyticsLink>
    </div>
    <img src={ BannerNODAcademy } alt="NOD Academy" />
  </div>,
  <div className="banner tplink" key="tplink">
    <div className="cta">
      <div className="info">
        <h3>{ t('products.tpLinkTitle') }</h3>
        <p>{ t('products.tpLinkDescription') }</p>
      </div>
      <AnalyticsLink
        to="https://support.omadanetworks.com/ro/emulator/"
        target="_blank"
        event={ { action: 'tp_link_banner_clicked' } }
      >
        { t('pages.seeMore') }
      </AnalyticsLink>
    </div>
    <img src={ BannerTPLink } alt="TP Link Platform" />
  </div>
];

const Banners = ({
  loading,
  services,
  actionHandlers = {},
}) => {
  const { t } = useTranslation();
  const { width } = useScreen();

  const getItems = () => {
    if (loading) {
      return [...Array(3).keys()].map(a => <StyledBannerPlaceholder key={ a } />);
    }

    if (services?.length > 0) {
      return services.map(service => (
        <div className="banner" key={ service?.type || service?.title }>
          <div className="cta">
            <div className="info">
              <h3>{ service?.title }</h3>
              <p>{ service?.description }</p>
            </div>
            { actionHandlers[service?.type]
              ? (
                <button
                  type="button"
                  onClick={ actionHandlers[service.type].onClick }
                >
                  { actionHandlers[service.type].loading && <Loading active small withOverlay={ false } /> }
                  { t('pages.seeMore') }
                </button>
              ) : (
                <AnalyticsLink
                  to={ service?.url }
                  target="_blank"
                  event={ { action: `${ service?.title }_clicked` } }
                >
                  { t('pages.seeMore') }
                </AnalyticsLink>
              )}
          </div>
          <Img
            src={ service.imageUrl }
            loader={ <Loading withOverlay={ false } active small /> }
            alt={ service?.title || 'Banner' }
          />
        </div>
      ));
    }

    return renderBackupBanners({ t, actionHandlers });
  };

  return (
    <StyledBanners>
      <Carousel
        className="bannersSlider"
        ssr
        customButtonGroup={ <ButtonGroup /> }
        autoPlaySpeed={ 3000 }
        deviceType="desktop"
        showDots={ false }
        autoPlay={ false }
        renderButtonGroupOutside
        arrows={ false }
        slidesToSlide={ 1 }
        removeArrowOnDeviceType={ ['tablet', 'mobile'] }
        shouldResetAutoplay={ width > 767 }
        responsive={ {
          desktop: {
            breakpoint: { max: 3000, min: 1368 },
            items: 3
          },
          tablet: {
            breakpoint: { max: 1368, min: 767 },
            items: 2
          },
          mobile: {
            breakpoint: { max: 767, min: 0 },
            items: 1,
          }
        } }
      >
        { getItems() }
      </Carousel>
    </StyledBanners>
  );
};

Banners.propTypes = {
  loading: bool,
  services: array,
  actionHandlers: objectOf(shape({
    onClick: func,
    loading: bool,
  })),
};

export default Banners;
